import React from 'react';

/**
 * Lightweight in-file markdown renderer tuned for The Green Room articles.
 * Deliberately no dependencies — we control the exact block/inline behaviour.
 *
 * Supported:
 *   ## Heading       → <h2>
 *   ### Sub-heading  → <h3>
 *   * item / - item / • item → <ul>
 *   **bold**         → <strong>
 *   *italic*, _italic_ → <em>
 *   blank line separates blocks
 */
const BULLET_RE = /^[\s]*[*\-•·●◦▪]{1,2}\s+(.*)$/;

function renderInline(text, keyPrefix = '') {
  // Handles **bold**, *italic*, _italic_. Escapes are unnecessary — content is admin-authored.
  const nodes = [];
  let i = 0;
  let idx = 0;
  const pushText = (t) => { if (t) nodes.push(t); };

  while (i < text.length) {
    // **bold**
    if (text.startsWith('**', i)) {
      const end = text.indexOf('**', i + 2);
      if (end !== -1) {
        nodes.push(<strong key={`${keyPrefix}-b-${idx++}`}>{text.slice(i + 2, end)}</strong>);
        i = end + 2;
        continue;
      }
    }
    // *italic* or _italic_
    const mkItalic = (delim) => {
      if (text[i] === delim && text[i + 1] !== delim) {
        const end = text.indexOf(delim, i + 1);
        if (end !== -1 && end !== i + 1) {
          const inner = text.slice(i + 1, end);
          if (!inner.includes('\n')) {
            nodes.push(<em key={`${keyPrefix}-i-${idx++}`}>{inner}</em>);
            i = end + 1;
            return true;
          }
        }
      }
      return false;
    };
    if (mkItalic('*') || mkItalic('_')) continue;

    // plain character — accumulate
    let j = i;
    while (j < text.length && text[j] !== '*' && text[j] !== '_') j++;
    pushText(text.slice(i, j));
    i = j;
  }
  return nodes;
}

export default function MarkdownBody({ body, className = '' }) {
  if (!body) return null;
  const normalised = body.replace(/\r\n/g, '\n').trim();
  const blocks = normalised.split(/\n\s*\n/); // split on blank lines
  const rendered = [];
  let bulletBuffer = null;

  const flushBullets = () => {
    if (bulletBuffer && bulletBuffer.length > 0) {
      rendered.push(
        <ul key={`ul-${rendered.length}`} className="list-disc pl-6 space-y-2 mb-6 text-[#333] leading-[1.85]">
          {bulletBuffer.map((li, i) => (
            <li key={`li-${rendered.length}-${i}`}>{renderInline(li, `li-${rendered.length}-${i}`)}</li>
          ))}
        </ul>
      );
    }
    bulletBuffer = null;
  };

  blocks.forEach((raw, idx) => {
    const block = raw.trim();
    if (!block) return;

    // Multi-line bullet list detection — every line must match the bullet regex
    const lines = block.split('\n').map((l) => l.trim()).filter(Boolean);
    if (lines.every((l) => BULLET_RE.test(l))) {
      if (!bulletBuffer) bulletBuffer = [];
      lines.forEach((l) => bulletBuffer.push(l.match(BULLET_RE)[1]));
      return;
    }
    flushBullets();

    if (block.startsWith('## ')) {
      rendered.push(
        <h2 key={`h2-${idx}`} className="font-heading uppercase text-[#002b5c] text-2xl md:text-3xl mt-12 mb-4 leading-tight">
          {renderInline(block.slice(3).trim(), `h2-${idx}`)}
        </h2>
      );
    } else if (block.startsWith('> ')) {
      rendered.push(
        <blockquote key={`q-${idx}`} data-testid="article-pull-quote" className="my-12 border-l-2 border-[#C5A059] pl-6 md:pl-8 font-heading uppercase text-[#002b5c] text-2xl md:text-3xl leading-tight">
          {renderInline(block.replace(/^>\s?/gm, '').trim(), `q-${idx}`)}
        </blockquote>
      );
    } else if (block.startsWith('### ')) {
      rendered.push(
        <h3 key={`h3-${idx}`} className="font-heading uppercase text-[#002b5c] text-xl md:text-2xl mt-10 mb-3 leading-tight">
          {renderInline(block.slice(4).trim(), `h3-${idx}`)}
        </h3>
      );
    } else {
      rendered.push(
        <p key={`p-${idx}`} className="mb-6 font-light text-[#333] leading-[1.85]">
          {renderInline(block, `p-${idx}`)}
        </p>
      );
    }
  });
  flushBullets();

  return <div className={className}>{rendered}</div>;
}
